import type { MapMarker } from '@gpx-spa/geo-domain';
import {
  accuracyRadiusMetersForRow,
  type V2CurrentPositionLike,
} from '@/map/operational/v2-current-positions-markers';
import { clusterPinPose } from './cluster-pin-pose';
import { convoyClusterMarkerId, convoyDeviceMarkerId } from './marker-ids';
import { namedSinglePopupHtml } from './popup-html';
import {
  resolveConvoyTrackerPresentation,
  type ConvoyTrackerPresentation,
} from './presentation';
import { convoyMarkerStyleForCount } from './styles';
import type { ConvoyCluster, ConvoyMemberInput } from './types';

export type ConvoyClustersToMapMarkersOptions = {
  /** Prefix marker ids to avoid collisions across shipments on one layer. */
  shipmentId?: number;
  presentation?: ConvoyTrackerPresentation;
};

/**
 * Presentation: ConvoyCluster[] → MapMarker[].
 * count=1 → named live-dot; count>1 → count pin (roster is Vue overlay).
 */
export function convoyClustersToMapMarkers(
  clusters: readonly ConvoyCluster[],
  options: ConvoyClustersToMapMarkersOptions = {},
): MapMarker[] {
  const shipmentId = options.shipmentId ?? 0;
  const presentation = resolveConvoyTrackerPresentation(options.presentation);
  const out: MapMarker[] = [];
  for (const cluster of clusters) {
    const count = cluster.memberDeviceIds.length;
    const pin = clusterPinPose(cluster.members);
    if (!pin) continue;
    const style = convoyMarkerStyleForCount(count);
    const pinMember = cluster.members.find((m) => m.deviceId === pin.deviceId);
    const row = pinMember?.source as V2CurrentPositionLike | undefined;
    const accuracyRadiusMeters =
      count === 1 && row ? accuracyRadiusMetersForRow(row) : undefined;
    const id =
      shipmentId > 0
        ? convoyClusterMarkerId(shipmentId, cluster.id)
        : `convoy-${cluster.id}`;

    const marker: MapMarker = {
      id,
      position: { lat: pin.lat, lng: pin.lng },
      style,
      ...(accuracyRadiusMeters != null ? { accuracyRadiusMeters } : {}),
    };
    if (count === 1) {
      marker.popup = namedSinglePopupHtml(
        presentation.label(pin.deviceId),
        row,
        false,
        presentation.exceptionTitles(pin.deviceId),
      );
      marker.popupHtml = true;
    }
    out.push(marker);
  }
  return out;
}

/** One named pin per member (expanded convoy mode). */
export function convoyMembersToDeviceMarkers(
  members: readonly ConvoyMemberInput[],
  options: ConvoyClustersToMapMarkersOptions = {},
): MapMarker[] {
  const shipmentId = options.shipmentId ?? 0;
  const presentation = resolveConvoyTrackerPresentation(options.presentation);
  const out: MapMarker[] = [];
  for (const member of members) {
    const row = member.source as V2CurrentPositionLike | undefined;
    const accuracyRadiusMeters = row
      ? accuracyRadiusMetersForRow(row)
      : undefined;
    const id =
      shipmentId > 0
        ? convoyDeviceMarkerId(shipmentId, member.deviceId)
        : `convoy-dev-${member.deviceId}`;
    out.push({
      id,
      position: { lat: member.lat, lng: member.lng },
      style: convoyMarkerStyleForCount(1),
      ...(accuracyRadiusMeters != null ? { accuracyRadiusMeters } : {}),
      popup: namedSinglePopupHtml(
        presentation.label(member.deviceId),
        row,
        true,
        presentation.exceptionTitles(member.deviceId),
      ),
      popupHtml: true,
    });
  }
  return out;
}

/** Map V2 current-position rows that belong to `deviceIds` into convoy members. */
export function membersFromV2CurrentPositions(
  rows: readonly V2CurrentPositionLike[],
  deviceIds: readonly number[],
): ConvoyMemberInput[] {
  const allowed = new Set(
    deviceIds.filter((id) => typeof id === 'number' && id > 0),
  );
  if (allowed.size === 0) return [];

  const out: ConvoyMemberInput[] = [];
  for (const row of rows) {
    if (!allowed.has(row.device_id)) continue;
    const loc = row.location;
    if (!loc || loc.length < 2) continue;
    const [lng, lat] = loc;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const ts = row.timestamp;
    out.push({
      deviceId: row.device_id,
      lat,
      lng,
      timestampSec: typeof ts === 'number' && Number.isFinite(ts) ? ts : 0,
      source: row,
    });
  }
  return out;
}
